import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EvidenceKind, IntegrityCase, IntegrityCaseAccessLogEntry, IntegrityCaseStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge, Tabs } from '../../ui/components';

const EVIDENCE_KINDS: EvidenceKind[] = ['DOCUMENT', 'TESTIMONY', 'FINANCIAL_RECORD', 'DIGITAL', 'PHYSICAL', 'OTHER'];
const CLOSING_STATUSES: IntegrityCaseStatus[] = ['CLOSED_INSUFFICIENT_EVIDENCE', 'CLOSED_UNSUBSTANTIATED', 'CLOSED_ADMIN_ACTION', 'CLOSED_REFERRED_OUT'];

/**
 * GET /integrity/cases/:id — 403s with a clear "not on this case's
 * investigation team" message if the signed-in investigator isn't LEAD or
 * MEMBER (requireCaseAccess). LEAD vs MEMBER is inferred client-side from
 * `investigators[]` matching persona.subject purely to decide which buttons
 * to render — the server re-checks LEAD-only actions independently
 * (requireLeadAccess), so this is a UI convenience, not the real gate.
 */
export function IntegrityCaseDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const [tab, setTab] = useState<'team' | 'evidence' | 'access-log'>('team');
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showAuditGrant, setShowAuditGrant] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const caseQ = useApiQuery<IntegrityCase>(id ? `/api/v1/m40/integrity/cases/${id}` : null);

  if (caseQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading case…</div>;
  if (caseQ.error) return <ErrorNote message={caseQ.error} />;
  const c = caseQ.data;
  if (!c) return null;

  const myMembership = c.investigators?.find((i) => !i.removedAt && i.subject === persona?.subject);
  const isLead = Boolean(myMembership && myMembership.role === 'LEAD');
  const isMember = Boolean(myMembership);
  const activeLeadCount = (c.investigators ?? []).filter((i) => !i.removedAt && i.role === 'LEAD').length;

  const post = async (path: string, body?: unknown) => {
    setBusy(path);
    setActionError(null);
    try {
      await api.post(`/api/v1/m40/integrity/cases/${c.id}/${path}`, body);
      caseQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    } finally {
      setBusy(null);
    }
  };

  const advanceTarget: IntegrityCaseStatus | null = c.status === 'OPENED' ? 'PRELIMINARY_REVIEW' : c.status === 'PRELIMINARY_REVIEW' ? 'FULL_INVESTIGATION' : null;
  const isTerminal = CLOSING_STATUSES.includes(c.status) || c.status === 'REFERRED_TO_PROSECUTION';

  return (
    <div>
      <PageHeader
        title={c.refNo}
        subtitle={`${c.subjectName}${c.subjectMda ? ` · ${c.subjectMda}` : ''} · ${c.category.replaceAll('_', ' ')}`}
        actions={<StatusBadge status={c.status} />}
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {!isMember ? (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
          You are viewing this because a service/admin call surfaced it, but you are not recorded on this case&rsquo;s investigation team — every
          action below will be rejected (403 — air-gapped need-to-know).
        </div>
      ) : (
        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-2.5 text-xs font-semibold text-indigo-800">
          Your access: {isLead ? 'LEAD' : 'MEMBER'}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Prosecution referral" value={c.prosecutionReferralStatus.replaceAll('_', ' ')} />
        <StatCard label="Audit grant" value={c.auditGrantId ? (c.auditGrantRevokedAt ? 'Revoked' : 'Active') : 'None'} />
        <StatCard label="Origin" value={c.originComplaintId ? 'Complaint referral' : c.originWhistleblowerReportId ? 'Whistleblower report' : 'Proactive'} />
        <StatCard label="Opened" value={formatDateTime(c.createdAt)} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Allegation summary</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{c.allegationSummary}</p>
        {c.closureSummary ? <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Closure: {c.closureSummary}</div> : null}
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {isMember && advanceTarget ? (
          <button className="btn-primary" onClick={() => post('advance', { to: advanceTarget })} disabled={busy !== null}>
            {busy === 'advance' ? 'Advancing…' : `Advance → ${advanceTarget.replaceAll('_', ' ')}`}
          </button>
        ) : null}
        {isLead && c.status === 'FULL_INVESTIGATION' ? (
          <button className="btn-success" onClick={() => post('refer-to-prosecution')} disabled={busy !== null}>
            {busy === 'refer-to-prosecution' ? 'Referring…' : 'Refer to Attorney General (M38)'}
          </button>
        ) : null}
        {isLead && !isTerminal ? (
          <button className="btn-danger" onClick={() => setShowClose(true)} disabled={busy !== null}>
            Close case
          </button>
        ) : null}
        {isLead ? (
          <button className="btn-secondary" onClick={() => setShowAuditGrant(true)} disabled={busy !== null}>
            {c.auditGrantId && !c.auditGrantRevokedAt ? 'Manage AuditChain grant' : 'Request AuditChain grant'}
          </button>
        ) : null}
      </div>

      <Tabs
        tabs={[
          { key: 'team', label: 'Investigation team' },
          { key: 'evidence', label: 'Evidence' },
          { key: 'access-log', label: 'Access log (LEAD only)' },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'team' ? (
        <div>
          {isLead ? (
            <div className="mb-3 flex justify-end">
              <button className="btn-primary" onClick={() => setShowAddTeam(true)}>
                + Add team member
              </button>
            </div>
          ) : null}
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Subject</th>
                  <th className="table-head">Role</th>
                  <th className="table-head">Added by</th>
                  <th className="table-head">Status</th>
                  {isLead ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {(c.investigators ?? []).map((inv) => (
                  <tr key={inv.id}>
                    <td className="table-cell font-mono text-xs">
                      {inv.subject}
                      {inv.subject === persona?.subject ? <span className="ml-1 text-slate-400">(you)</span> : null}
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={inv.role} />
                    </td>
                    <td className="table-cell text-xs">{inv.addedBy}</td>
                    <td className="table-cell text-xs">{inv.removedAt ? `Removed ${formatDateTime(inv.removedAt)}` : 'Active'}</td>
                    {isLead ? (
                      <td className="table-cell text-right">
                        {!inv.removedAt && !(inv.role === 'LEAD' && activeLeadCount <= 1) ? (
                          <button
                            className="btn-danger !px-2.5 !py-1 text-xs"
                            disabled={busy !== null}
                            onClick={() => post(`team/${inv.id}/remove`)}
                          >
                            Remove
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'evidence' ? (
        <div>
          {isMember ? (
            <div className="mb-3 flex justify-end">
              <button className="btn-primary" onClick={() => setShowEvidence(true)}>
                + Add evidence
              </button>
            </div>
          ) : null}
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Title</th>
                  <th className="table-head">Kind</th>
                  <th className="table-head">SHA-256</th>
                  <th className="table-head">Collected</th>
                </tr>
              </thead>
              <tbody>
                {(c.evidenceItems ?? []).length === 0 ? (
                  <tr>
                    <td className="table-cell text-xs text-slate-400" colSpan={4}>
                      No evidence recorded yet.
                    </td>
                  </tr>
                ) : (
                  (c.evidenceItems ?? []).map((ev) => (
                    <tr key={ev.id}>
                      <td className="table-cell font-medium">{ev.title}</td>
                      <td className="table-cell text-xs">{ev.kind.replaceAll('_', ' ')}</td>
                      <td className="table-cell font-mono text-[11px] text-slate-400">{ev.sha256.slice(0, 16)}…</td>
                      <td className="table-cell text-xs">{formatDateTime(ev.collectedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Never issued through DocVault — evidence stays inside this internal-only registry (see this module&rsquo;s design notes).
          </p>
        </div>
      ) : (
        <AccessLogPanel caseId={c.id} isLead={isLead} />
      )}

      {showAddTeam ? (
        <AddTeamModal
          onClose={() => setShowAddTeam(false)}
          onSubmit={async (body) => {
            await post('team', body);
            setShowAddTeam(false);
          }}
        />
      ) : null}
      {showEvidence ? (
        <AddEvidenceModal
          onClose={() => setShowEvidence(false)}
          onSubmit={async (body) => {
            await post('evidence', body);
            setShowEvidence(false);
          }}
        />
      ) : null}
      {showClose ? (
        <CloseModal
          onClose={() => setShowClose(false)}
          onSubmit={async (body) => {
            await post('close', body);
            setShowClose(false);
          }}
        />
      ) : null}
      {showAuditGrant ? (
        <AuditGrantModal
          caseRow={c}
          onClose={() => setShowAuditGrant(false)}
          onRequest={async (body) => {
            await post('audit-grant', body);
            setShowAuditGrant(false);
          }}
          onRevoke={async () => {
            await post('audit-grant/revoke');
            setShowAuditGrant(false);
          }}
        />
      ) : null}
    </div>
  );
}

function AccessLogPanel({ caseId, isLead }: { caseId: string; isLead: boolean }) {
  const logQ = useApiQuery<{ items: IntegrityCaseAccessLogEntry[] }>(isLead ? `/api/v1/m40/integrity/cases/${caseId}/access-log` : null);
  if (!isLead) {
    return <p className="text-xs text-slate-400">LEAD-only — investigator-auditable trail of every read of this case, never citizen-visible.</p>;
  }
  return (
    <div className="card overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className="table-head">Actor</th>
            <th className="table-head">Roles</th>
            <th className="table-head">Action</th>
            <th className="table-head">Context</th>
            <th className="table-head">When</th>
          </tr>
        </thead>
        <tbody>
          {(logQ.data?.items.length ?? 0) === 0 ? (
            <tr>
              <td className="table-cell text-xs text-slate-400" colSpan={5}>
                No access log entries yet.
              </td>
            </tr>
          ) : (
            logQ.data!.items.map((l) => (
              <tr key={l.id}>
                <td className="table-cell font-mono text-xs">{l.actorSubject}</td>
                <td className="table-cell text-xs">{l.actorRoles.join(', ')}</td>
                <td className="table-cell text-xs">{l.action}</td>
                <td className="table-cell text-xs">{l.context ?? '—'}</td>
                <td className="table-cell text-xs">{formatDateTime(l.createdAt)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function AddTeamModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { subject: string; role: 'LEAD' | 'MEMBER' }) => Promise<void> }) {
  const [subject, setSubject] = useState('');
  const [role, setRole] = useState<'LEAD' | 'MEMBER'>('MEMBER');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ subject, role });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add team member');
      setBusy(false);
    }
  };

  return (
    <Modal title="Add investigation team member" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Investigator subject">
          <input className="field-input font-mono" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. acc_nolan_adeline" />
        </Field>
        <p className="text-xs text-slate-500">
          There is no staff-directory lookup in this module — the LEAD must know the other investigator&rsquo;s SeyID subject string directly (a
          real gap; dev personas use the <code>acc_&lt;name&gt;</code> convention from server/personas.ts).
        </p>
        <Field label="Role">
          <select className="field-input" value={role} onChange={(e) => setRole(e.target.value as 'LEAD' | 'MEMBER')}>
            <option value="MEMBER">Member</option>
            <option value="LEAD">Lead</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || subject.trim().length < 2}>
            {busy ? 'Adding…' : 'Add to team'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddEvidenceModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: Record<string, unknown>) => Promise<void> }) {
  const [kind, setKind] = useState<EvidenceKind>('DOCUMENT');
  const [title, setTitle] = useState('');
  const [storageRef, setStorageRef] = useState('');
  const [sha256, setSha256] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ kind, title, storageRef, sha256, notes: notes || undefined, collectedAt: new Date().toISOString() });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add evidence');
      setBusy(false);
    }
  };

  const valid = title.trim().length >= 2 && storageRef.trim().length >= 1 && /^[0-9a-f]{64}$/i.test(sha256.trim());

  return (
    <Modal title="Add evidence item" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kind">
            <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as EvidenceKind)}>
              {EVIDENCE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Title">
            <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        </div>
        <Field label="Storage reference (evidence-locker ref, not DocVault)">
          <input className="field-input" value={storageRef} onChange={(e) => setStorageRef(e.target.value)} placeholder="e.g. locker://accs/2026/EX-014" />
        </Field>
        <Field label="SHA-256 (64 hex characters)">
          <input className="field-input font-mono" value={sha256} onChange={(e) => setSha256(e.target.value)} placeholder="hash of the underlying file" />
        </Field>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Adding…' : 'Add evidence'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CloseModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { status: IntegrityCaseStatus; closureSummary: string }) => Promise<void> }) {
  const [status, setStatus] = useState<IntegrityCaseStatus>('CLOSED_UNSUBSTANTIATED');
  const [closureSummary, setClosureSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ status, closureSummary });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to close case');
      setBusy(false);
    }
  };

  return (
    <Modal title="Close integrity case" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Closing status">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as IntegrityCaseStatus)}>
            {CLOSING_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Closure summary">
          <textarea className="field-input" rows={3} value={closureSummary} onChange={(e) => setClosureSummary(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || closureSummary.trim().length < 5}>
            {busy ? 'Closing…' : 'Close case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AuditGrantModal({
  caseRow,
  onClose,
  onRequest,
  onRevoke,
}: {
  caseRow: IntegrityCase;
  onClose: () => void;
  onRequest: (body: { scopeQuery: { modules?: string[]; nins?: string[] }; courtOrderRef: string; expiresAt: string }) => Promise<void>;
  onRevoke: () => Promise<void>;
}) {
  const [modules, setModules] = useState('');
  const [nins, setNins] = useState('');
  const [courtOrderRef, setCourtOrderRef] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hasActiveGrant = Boolean(caseRow.auditGrantId && !caseRow.auditGrantRevokedAt);

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      const moduleList = modules.split(',').map((s) => s.trim()).filter(Boolean);
      const ninList = nins.split(',').map((s) => s.trim()).filter(Boolean);
      await onRequest({
        scopeQuery: { modules: moduleList.length ? moduleList : undefined, nins: ninList.length ? ninList : undefined },
        courtOrderRef,
        expiresAt: new Date(expiresAt).toISOString(),
      });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await onRevoke();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revoke failed');
      setBusy(false);
    }
  };

  return (
    <Modal title="AuditChain (C6) investigator grant" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {hasActiveGrant ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Active grant {caseRow.auditGrantId} — court order {caseRow.auditGrantCourtOrderRef}, expires {formatDateTime(caseRow.auditGrantExpiresAt)}.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn-danger" onClick={revoke} disabled={busy}>
                {busy ? 'Revoking…' : 'Revoke grant'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">A real, live C6 AuditChain call — a scoped investigator grant backed by a court order reference.</p>
            <Field label="Modules in scope (comma separated, optional if NINs given)">
              <input className="field-input" value={modules} onChange={(e) => setModules(e.target.value)} placeholder="e.g. f06-payments, c2-seypay" />
            </Field>
            <Field label="NINs in scope (comma separated, optional if modules given)">
              <input className="field-input" value={nins} onChange={(e) => setNins(e.target.value)} placeholder="e.g. SC700022013" />
            </Field>
            <Field label="Court order reference">
              <input className="field-input" value={courtOrderRef} onChange={(e) => setCourtOrderRef(e.target.value)} />
            </Field>
            <Field label="Expires at">
              <input type="datetime-local" className="field-input" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={request}
                disabled={busy || (!modules.trim() && !nins.trim()) || courtOrderRef.trim().length < 2 || !expiresAt}
              >
                {busy ? 'Requesting…' : 'Request grant'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
